import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  FASTCASHTRADE - MASTER ENGINEER SPLASH SCREEN                            ║
 * ║  Concept: "The Vault Opens"                                               ║
 * ║  Philosophy: Simplicity with Depth. Trust through Design.                 ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 * 
 * DESIGN PRINCIPLES:
 * 1. First Impression = Last Impression
 * 2. Motion serves meaning, not decoration
 * 3. Every pixel earns its place
 * 4. 60fps or nothing
 * 
 * BRAND PSYCHOLOGY:
 * - TRUST: Symmetry, stability, centered composition
 * - SPEED: Sharp transitions, no unnecessary delays
 * - SECURITY: Vault metaphor, contained energy
 * - PREMIUM: Negative space, restrained palette
 */

const SplashScreen: React.FC = () => {
    const [isRevealed, setIsRevealed] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    // === BRAND DNA ===
    const colors = {
        void: '#000000',        // True black - the void before creation
        obsidian: '#0A0D12',    // Deep background
        platinum: '#FDFDFD',    // Primary brand white
        titanium: '#A4A7AE',    // Secondary gray
        accent: 'rgba(255,255,255,0.08)' // Subtle accents
    };

    useEffect(() => {
        // Reveal sequence timing
        const revealTimer = setTimeout(() => setIsRevealed(true), 800);
        const completeTimer = setTimeout(() => setIsComplete(true), 3500);
        return () => {
            clearTimeout(revealTimer);
            clearTimeout(completeTimer);
        };
    }, []);

    // === MOTION CURVES (Custom Bézier for Premium Feel) ===
    const curves = {
        reveal: [0.16, 1, 0.3, 1] as [number, number, number, number],      // Smooth deceleration
        breathe: [0.4, 0, 0.2, 1] as [number, number, number, number],       // Natural breathing
        sharp: [0.7, 0, 0.3, 1] as [number, number, number, number]          // Precise, intentional
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 1 }}
                exit={{
                    opacity: 0,
                    transition: { duration: 0.6, ease: curves.breathe }
                }}
                className="fixed inset-0 z-[9999] overflow-hidden"
                style={{
                    backgroundColor: colors.void,
                    willChange: 'opacity'
                }}
            >
                {/* ═══ LAYER 0: THE VOID ═══ */}
                {/* Pure black. The canvas before the brand exists. */}

                {/* ═══ LAYER 1: AMBIENT FIELD ═══ */}
                {/* Subtle radial gradient that breathes - gives depth without distraction */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isRevealed ? 1 : 0 }}
                    transition={{ duration: 2, ease: curves.breathe }}
                    className="absolute inset-0"
                    style={{
                        background: `radial-gradient(ellipse 80% 50% at 50% 50%, ${colors.obsidian} 0%, ${colors.void} 100%)`
                    }}
                />

                {/* ═══ LAYER 2: THE VAULT DOORS ═══ */}
                {/* Two panels that slide apart to reveal the brand */}
                <motion.div
                    initial={{ x: 0 }}
                    animate={{ x: isRevealed ? '-52%' : 0 }}
                    transition={{ duration: 1.2, ease: curves.reveal, delay: 0.3 }}
                    className="absolute inset-y-0 left-0 w-1/2"
                    style={{
                        backgroundColor: colors.void,
                        willChange: 'transform',
                        boxShadow: isRevealed ? '20px 0 60px rgba(0,0,0,0.8)' : 'none'
                    }}
                />
                <motion.div
                    initial={{ x: 0 }}
                    animate={{ x: isRevealed ? '52%' : 0 }}
                    transition={{ duration: 1.2, ease: curves.reveal, delay: 0.3 }}
                    className="absolute inset-y-0 right-0 w-1/2"
                    style={{
                        backgroundColor: colors.void,
                        willChange: 'transform',
                        boxShadow: isRevealed ? '-20px 0 60px rgba(0,0,0,0.8)' : 'none'
                    }}
                />

                {/* ═══ LAYER 3: CENTER SEAM LIGHT ═══ */}
                {/* A thin vertical light that appears before the vault opens */}
                <motion.div
                    initial={{ scaleY: 0, opacity: 0 }}
                    animate={{
                        scaleY: isRevealed ? 0 : 1,
                        opacity: isRevealed ? 0 : [0, 1, 1, 0]
                    }}
                    transition={{
                        duration: 0.8,
                        times: [0, 0.3, 0.7, 1]
                    }}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[2px] h-[40vh]"
                    style={{
                        background: `linear-gradient(to bottom, transparent, ${colors.platinum}, transparent)`,
                        filter: 'blur(1px)'
                    }}
                />

                {/* ═══ LAYER 4: THE BRAND CORE ═══ */}
                {/* The heart of the experience - revealed by the vault doors */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">

                    {/* FCT Monogram */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{
                            opacity: isRevealed ? 1 : 0,
                            scale: isRevealed ? 1 : 0.9
                        }}
                        transition={{ duration: 1, ease: curves.reveal, delay: 0.6 }}
                        className="flex items-center mb-8"
                    >
                        <span
                            className="text-7xl md:text-8xl lg:text-9xl font-black italic tracking-tight select-none"
                            style={{
                                color: colors.platinum,
                                textShadow: '0 0 80px rgba(255,255,255,0.1)'
                            }}
                        >
                            F
                        </span>
                        <span
                            className="text-7xl md:text-8xl lg:text-9xl font-black italic tracking-tight select-none"
                            style={{ color: colors.platinum }}
                        >
                            C
                        </span>
                        <span
                            className="text-7xl md:text-8xl lg:text-9xl font-black italic tracking-tight select-none ml-1"
                            style={{ color: colors.titanium, opacity: 0.7 }}
                        >
                            T
                        </span>
                    </motion.div>

                    {/* Integrity Line */}
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: isRevealed ? 1 : 0 }}
                        transition={{ duration: 1.2, ease: curves.sharp, delay: 1.2 }}
                        className="mt-6 h-[1px] w-24 origin-center"
                        style={{ backgroundColor: colors.accent }}
                    />

                    {/* Brand Name - Now positioned below FCT */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                            opacity: isRevealed ? 1 : 0,
                            y: isRevealed ? 0 : 10
                        }}
                        transition={{ duration: 0.8, ease: curves.reveal, delay: 1.5 }}
                        className="mt-6 flex items-center text-xl md:text-2xl font-black italic tracking-tight"
                    >
                        <span style={{ color: colors.platinum }}>FASTCASH</span>
                        <span style={{ color: colors.titanium }}>TRADE</span>
                    </motion.div>

                    {/* Platform Tagline */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isComplete ? 0.5 : 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mt-4 text-[9px] font-medium tracking-[0.3em] uppercase"
                        style={{ color: colors.titanium }}
                    >
                        Güvenli Alışveriş Platformu
                    </motion.p>
                </div>

                {/* ═══ LAYER 5: BREATHING PULSE ═══ */}
                {/* Subtle pulse that indicates "life" after reveal */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                        opacity: isComplete ? [0, 0.03, 0] : 0,
                        scale: isComplete ? [1, 1.5, 1] : 1
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: `radial-gradient(circle at 50% 50%, ${colors.platinum} 0%, transparent 50%)`
                    }}
                />

                {/* ═══ LAYER 5.5: THE TRUST ARC (Shooting Star) ═══ */}
                {/* 
                 * Disney has magic dust. We have the Trust Arc.
                 * This arc represents the secure transaction path - 
                 * the invisible bridge between buyer and seller.
                 * It sweeps across, "blessing" the brand with security.
                 */}
                <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <defs>
                        {/* Arc Gradient - Fades at both ends */}
                        <linearGradient id="arcGlow" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="transparent" />
                            <stop offset="30%" stopColor={colors.platinum} stopOpacity="0.6" />
                            <stop offset="50%" stopColor={colors.platinum} stopOpacity="1" />
                            <stop offset="70%" stopColor={colors.platinum} stopOpacity="0.6" />
                            <stop offset="100%" stopColor="transparent" />
                        </linearGradient>
                        {/* Glow Filter */}
                        <filter id="starGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {/* The Arc Path - Sweeps from bottom-left to top-center to bottom-right */}
                    <motion.path
                        d="M 15 70 Q 50 20 85 70"
                        fill="transparent"
                        stroke="url(#arcGlow)"
                        strokeWidth="0.3"
                        strokeLinecap="round"
                        filter="url(#starGlow)"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{
                            pathLength: isRevealed ? [0, 1] : 0,
                            opacity: isRevealed ? [0, 0.8, 0] : 0
                        }}
                        transition={{
                            duration: 1.8,
                            delay: 1.8,
                            ease: [0.16, 1, 0.3, 1] as [number, number, number, number]
                        }}
                    />

                    {/* The Star Head - The bright point that leads the arc */}
                    <motion.circle
                        r="0.8"
                        fill={colors.platinum}
                        filter="url(#starGlow)"
                        initial={{ opacity: 0, cx: 15, cy: 70 }}
                        animate={{
                            opacity: isRevealed ? [0, 1, 1, 0] : 0,
                            cx: isRevealed ? [15, 50, 85] : 15,
                            cy: isRevealed ? [70, 20, 70] : 70
                        }}
                        transition={{
                            duration: 1.8,
                            delay: 1.8,
                            ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
                            times: [0, 0.5, 1]
                        }}
                        style={{
                            filter: `drop-shadow(0 0 3px ${colors.platinum})`
                        }}
                    />

                    {/* Sparkle Particles - Left behind by the star */}
                    {[0, 1, 2].map((i) => (
                        <motion.circle
                            key={i}
                            r="0.3"
                            fill={colors.platinum}
                            initial={{ opacity: 0, cx: 40 + i * 10, cy: 35 + i * 5 }}
                            animate={{
                                opacity: isRevealed ? [0, 0.6, 0] : 0,
                                cy: isRevealed ? [35 + i * 5, 30 + i * 5] : 35 + i * 5
                            }}
                            transition={{
                                duration: 1.2,
                                delay: 2.2 + i * 0.15,
                                ease: "easeOut"
                            }}
                            cx={40 + i * 10}
                        />
                    ))}
                </svg>

                {/* ═══ LAYER 6: PLATFORM SIGNATURE ═══ */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isComplete ? 0.15 : 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="absolute bottom-10 left-0 right-0 flex justify-center"
                >
                    <div className="flex items-center gap-3 text-[8px] font-medium tracking-[0.3em] uppercase" style={{ color: colors.titanium }}>
                        <span>Secure</span>
                        <div className="w-1 h-1 rounded-full" style={{ backgroundColor: colors.accent }} />
                        <span>Fast</span>
                        <div className="w-1 h-1 rounded-full" style={{ backgroundColor: colors.accent }} />
                        <span>Trusted</span>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SplashScreen;
